import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { IconV2Props } from '@withjoy/joykit';
import { ButtonV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { createDrawerAnimation } from './drawerAnimationBase';
import { DrawerContent, DrawerLayout, ScrollTopContainer } from './MobileTransparentDrawer.styles';

type MobileTransparentDrawerProps = Readonly<{
  children: React.ReactNode;
  isOpenByDefault?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  scrollTopCta: {
    icon: React.FC<IconV2Props>;
    text: string;
    closeOnClick: boolean;
  };
  closedHeightPx?: number;
  openPositionVh?: number;
  openPositionOffsetPx?: number;
  animationType?: 'css' | 'spring';
  /** Optional sheet background override (e.g. the cash-fund cream); defaults to white. */
  backgroundColor?: string;
  /** When true, the scroll CTA button is visible immediately when the drawer opens,
   *  not only after the user scrolls down 100px. */
  showScrollCtaByDefault?: boolean;
}>;

const CLOSED_HEIGHT = 76;
const DEFAULT_OPEN_POSITION_VH = 50; // 50vh offset when open

export const MobileTransparentDrawer = ({
  children,
  scrollTopCta,
  isOpenByDefault = false,
  onOpenChange,
  closedHeightPx = CLOSED_HEIGHT,
  openPositionVh = DEFAULT_OPEN_POSITION_VH,
  openPositionOffsetPx = 0,
  animationType = 'css',
  backgroundColor,
  showScrollCtaByDefault = false,
}: MobileTransparentDrawerProps) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [showScrollTop, setShowScrollTop] = useState(showScrollCtaByDefault && isOpenByDefault);

  const containerRef = useRef<HTMLDivElement>(null);

  const ScrollTopCtaIcon = scrollTopCta.icon;

  const { Wrapper: DrawerWrapper, useAnimation, baseProps } = useMemo(() => createDrawerAnimation(animationType), [animationType]);

  const style = useAnimation({ isOpen, openPositionVh, openPositionOffsetPx, closedHeightPx });

  const handleClose = useEventCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
    if (containerRef.current) {
      // Toggling overflow stops iOS momentum scrolling which runs on a
      // separate compositor thread and ignores scrollTo during momentum.
      const container = containerRef.current;
      container.style.overflow = 'hidden';
      container.scrollTop = 0;
      container.style.overflow = '';
    }
  }, [onOpenChange]);

  const handleOpen = useEventCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      event.stopPropagation();
      setIsOpen(true);
      onOpenChange?.(true);
    },
    [onOpenChange],
  );

  // Scroll handler for showing scroll-to-top button
  const handleCheckScrollTopButtonVisibility = useCallback(
    (event?: React.UIEvent<HTMLDivElement>) => {
      if (!containerRef.current || !isOpen) {
        return;
      }

      if (showScrollCtaByDefault) {
        setShowScrollTop(true);
        return;
      }

      if (event?.currentTarget && event.currentTarget !== containerRef.current) {
        setShowScrollTop(event.currentTarget.scrollTop > 100);
        return;
      }

      setShowScrollTop(containerRef.current.scrollTop > 100);
    },
    [isOpen, showScrollCtaByDefault],
  );

  useEffect(() => {
    if (!isOpen) {
      setShowScrollTop(false);
      return;
    }

    if (showScrollCtaByDefault) {
      setShowScrollTop(true);
      return;
    }

    handleCheckScrollTopButtonVisibility();
  }, [handleCheckScrollTopButtonVisibility, isOpen, showScrollCtaByDefault]);

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <DrawerLayout
        ref={containerRef}
        onClick={handleClose}
        onScroll={handleCheckScrollTopButtonVisibility}
        $isOpen={isOpen}
      >
        <DrawerWrapper
          {...baseProps}
          style={style}
        >
          {/* Header handle - sticky so it stays visible when scrolling */}
          <DrawerContent
            $backgroundColor={backgroundColor}
            style={
              {
                '--drawer-open-position-vh': openPositionVh,
                '--drawer-open-position-offset-px': `${openPositionOffsetPx}px`,
              } as React.CSSProperties
            }
            onClick={handleOpen}
          >
            {children}
          </DrawerContent>
        </DrawerWrapper>
      </DrawerLayout>
      {showScrollTop && isOpen && (
        <ScrollTopContainer>
          <ButtonV2
            intent="neutral"
            minWidth={pxToRem(164)}
            paddingY={4}
            typographyVariant="button2"
            shape="rounded"
            startIcon={<ScrollTopCtaIcon />}
            onClick={scrollTopCta.closeOnClick ? handleClose : scrollToTop}
            size="md"
          >
            {scrollTopCta.text}
          </ButtonV2>
        </ScrollTopContainer>
      )}
    </>
  );
};
